/**
 * Uploads, headless and shared: file validation by kind, HEIC → JPEG
 * on pick, a queue that uploads 3 at a time with per-file progress, retry and
 * remove, and the `documents[]` it produces. Uploads start only when the
 * screen calls `uploadPending()` (on "Add item to vault"); a retry or a
 * resubmit skips files that are already done. The Storage transport is an
 * `UploadAdapter` (Firebase in `./firebase_storage.ts`, a fake in tests).
 *
 * Shared by add item, collector items, item documents, claims and support.
 */

/** Collector document kinds: each is also the stored `documents[].type`. */
export const COLLECTOR_UPLOAD_KINDS = [
  "invoice",
  "valuation_report",
  "provenance_records",
  "condition_report",
  "storage_statement",
  "proof_of_external_storage",
  "selfie_with_item",
  "item_image",
] as const;
export type CollectorUploadKind = (typeof COLLECTOR_UPLOAD_KINDS)[number];

export type UploadKind = "photo" | "receipt" | CollectorUploadKind;

/** Every kind, in the order `documents()` lists them. */
export const UPLOAD_KINDS: readonly UploadKind[] = ["photo", "receipt", ...COLLECTOR_UPLOAD_KINDS];

/** At most this many documents on one item (the backend's `MAX_DOCS`). */
export const MAX_TOTAL_FILES = 30;

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export const HEIC_TYPES: readonly string[] = ["image/heic", "image/heif"];

export interface KindLimits {
  min: number;
  max: number;
  /** Accepted MIME types (HEIC is converted to JPEG before upload). */
  types: readonly string[];
}

const IMAGE_TYPES: readonly string[] = ["image/jpeg", "image/png", ...HEIC_TYPES];
const DOCUMENT_TYPES: readonly string[] = [...IMAGE_TYPES, "application/pdf"];
const collectorDocs: KindLimits = { min: 0, max: 10, types: DOCUMENT_TYPES };
const collectorImages: KindLimits = { min: 0, max: 10, types: IMAGE_TYPES };

export const UPLOAD_LIMITS: Record<UploadKind, KindLimits> = {
  photo: { min: 1, max: 10, types: IMAGE_TYPES },
  receipt: { min: 0, max: 5, types: DOCUMENT_TYPES },
  invoice: collectorDocs,
  valuation_report: collectorDocs,
  provenance_records: collectorDocs,
  condition_report: collectorDocs,
  storage_statement: collectorDocs,
  proof_of_external_storage: collectorDocs,
  selfie_with_item: collectorImages,
  item_image: collectorImages,
};

/** `accept` for a file input of this kind (types plus extensions for HEIC and PDF). */
export function acceptFor(kind: UploadKind): string {
  const types = UPLOAD_LIMITS[kind].types;
  return [...types, ".heic", ".heif", ...(types.includes("application/pdf") ? [".pdf"] : [])].join(",");
}

export type DocumentClassification = "item" | "selfie" | "document" | Exclude<CollectorUploadKind, "item_image" | "selfie_with_item" | "invoice">;

/** The stored `documents[]` `type` and `classification` for each kind. */
export const DOCUMENT_SHAPE: Record<UploadKind, { type: CollectorUploadKind; classification: DocumentClassification }> = {
  photo: { type: "item_image", classification: "item" },
  receipt: { type: "invoice", classification: "document" },
  item_image: { type: "item_image", classification: "item" },
  selfie_with_item: { type: "selfie_with_item", classification: "selfie" },
  invoice: { type: "invoice", classification: "document" },
  valuation_report: { type: "valuation_report", classification: "valuation_report" },
  provenance_records: { type: "provenance_records", classification: "provenance_records" },
  condition_report: { type: "condition_report", classification: "condition_report" },
  storage_statement: { type: "storage_statement", classification: "storage_statement" },
  proof_of_external_storage: { type: "proof_of_external_storage", classification: "proof_of_external_storage" },
};

/** The stored object's extension and content type (never HEIC). */
const STORED: Record<string, { ext: "jpg" | "png" | "pdf"; contentType: string }> = {
  "image/jpeg": { ext: "jpg", contentType: "image/jpeg" },
  "image/png": { ext: "png", contentType: "image/png" },
  "application/pdf": { ext: "pdf", contentType: "application/pdf" },
};

export type FileError = "tooBig" | "badType" | "heicFailed" | "upload" | "unavailable";

export type FileStatus =
  /** HEIC being converted ("Preparing {name}…"). */
  | "preparing"
  /** Valid, waiting for submit. */
  | "ready"
  | "uploading"
  | "done"
  | "failed"
  /** Rejected before upload (size/type/HEIC); shown, never queued. */
  | "invalid";

export interface QueuedFile {
  id: string;
  kind: UploadKind;
  /** The file name the customer picked. */
  name: string;
  status: FileStatus;
  /** 0–100 while uploading. */
  progress: number;
  error?: FileError;
  /** Set once uploaded. */
  result?: { url: string; location: string };
  /** What gets uploaded (HEIC already converted). */
  file?: Blob;
  contentType?: string;
  ext?: string;
}

export interface UploadRequest {
  /** `{uuid}.{ext}`; the adapter prepends the caller's `pathPrefix`. */
  objectName: string;
  contentType: string;
  /** The signed-in customer (the adapter signs in again if it changes). */
  ownerId: string | null;
  onProgress: (percent: number) => void;
}

export interface UploadAdapter {
  upload(file: Blob, req: UploadRequest): Promise<{ url: string; location: string }>;
}

/**
 * Deletes a stored object (item documents, W-6). Only an object under the
 * signed-in owner's `pathPrefix` is deleted (false otherwise); a missing
 * object counts as deleted. Callers delete only after the server confirmed
 * the document is gone from the item, and treat failures as best effort.
 */
export interface StorageRemover {
  remove(ownerId: string | null, location: string): Promise<boolean>;
}

/**
 * Storage isn't usable: no bucket for this deploy, or no file in a batch
 * uploaded (Storage down, live rules missing). Shown as a RecoveryView.
 */
export class UploadsUnavailableError extends Error {
  readonly code = "uploads_unavailable";
  constructor() {
    super("Uploads are not configured");
    this.name = "UploadsUnavailableError";
  }
}

export type HeicConverter = (file: File) => Promise<File>;

/** HEIC by MIME, or by extension when the browser reports no type. */
export function isHeic(file: Pick<File, "name" | "type">): boolean {
  if (HEIC_TYPES.includes(file.type.toLowerCase())) return true;
  return file.type === "" && /\.(heic|heif)$/i.test(file.name);
}

export function validateFile(file: Pick<File, "name" | "type" | "size">, kind: UploadKind): FileError | null {
  const heic = isHeic(file);
  if (!heic && !UPLOAD_LIMITS[kind].types.includes(file.type.toLowerCase())) return "badType";
  // The limit applies to the original file (HEIC before conversion), and the
  // Storage rules require strictly under 10 MB.
  if (file.size >= MAX_FILE_BYTES) return "tooBig";
  return null;
}

export interface UploadQueueState {
  files: QueuedFile[];
  /** Set when the last `add` of that kind dropped files over the max. */
  tooMany: Partial<Record<UploadKind, boolean>>;
  /** Set when the last `add` of that kind dropped files over `MAX_TOTAL_FILES` (`uploads.limits.total`). */
  overTotal: Partial<Record<UploadKind, boolean>>;
}

export interface UploadQueue {
  getState(): UploadQueueState;
  subscribe(cb: () => void): () => void;
  /** Validates, converts HEIC, keeps the first files up to the kind's max. */
  add(kind: UploadKind, files: readonly File[]): Promise<void>;
  remove(id: string): void;
  /** Uploads every ready/failed file (3 at a time). True when all are done. */
  uploadPending(ownerId: string | null): Promise<boolean>;
  /** Uploads one failed file again. True when every file is now done. */
  retry(id: string, ownerId: string | null): Promise<boolean>;
  /** Valid files (not invalid/preparing) of a kind. */
  count(kind: UploadKind): number;
  /** Every valid file is uploaded. */
  allDone(): boolean;
  /** Any file still converting. */
  preparing(): boolean;
  /** `documents[]` for `POST /items`, in pick order per kind. */
  documents(): ItemUploadDocument[];
  /** The last error worth a RecoveryView: token, config, or a batch where no file uploaded. */
  lastError(): unknown;
  clear(): void;
}

export interface ItemUploadDocument {
  url: string;
  location: string;
  type: CollectorUploadKind;
  classification: DocumentClassification;
  order_index: number;
}

export interface CreateUploadQueueOptions {
  adapter: UploadAdapter;
  convertHeic?: HeicConverter;
  uuid?: () => string;
  concurrency?: number;
}

export const UPLOAD_CONCURRENCY = 3;

export function createUploadQueue(opts: CreateUploadQueueOptions): UploadQueue {
  const uuid = opts.uuid ?? (() => globalThis.crypto.randomUUID());
  const concurrency = opts.concurrency ?? UPLOAD_CONCURRENCY;
  let files: QueuedFile[] = [];
  let tooMany: UploadQueueState["tooMany"] = {};
  let overTotal: UploadQueueState["overTotal"] = {};
  let state: UploadQueueState = { files, tooMany, overTotal };
  let lastError: unknown = null;
  let seq = 0;
  const subs = new Set<() => void>();
  const emit = () => {
    state = { files, tooMany, overTotal };
    for (const s of subs) s();
  };
  const patch = (id: string, p: Partial<QueuedFile>) => {
    files = files.map((f) => (f.id === id ? { ...f, ...p } : f));
    emit();
  };
  const valid = (f: QueuedFile) => f.status !== "invalid";
  const get = (id: string) => files.find((f) => f.id === id);

  const prepare = async (row: QueuedFile, file: File) => {
    let out: File = file;
    if (isHeic(file)) {
      try {
        if (!opts.convertHeic) throw new Error("no HEIC converter");
        out = await opts.convertHeic(file);
      } catch {
        if (get(row.id)) patch(row.id, { status: "invalid", error: "heicFailed" });
        return;
      }
    }
    const stored = STORED[out.type.toLowerCase()];
    if (!get(row.id)) return; // removed while converting
    // A converted JPEG can outgrow the limit; Storage would deny every upload.
    if (out.size >= MAX_FILE_BYTES) {
      patch(row.id, { status: "invalid", error: "tooBig" });
      return;
    }
    if (!stored) {
      patch(row.id, { status: "invalid", error: "badType" });
      return;
    }
    patch(row.id, { status: "ready", file: out, contentType: stored.contentType, ext: stored.ext });
  };

  const uploadOne = async (id: string, ownerId: string | null): Promise<boolean> => {
    const row = get(id);
    if (!row || !row.file || !row.contentType || !row.ext) return false;
    if (row.status === "done") return true;
    patch(id, { status: "uploading", progress: 0, error: undefined });
    try {
      const result = await opts.adapter.upload(row.file, {
        objectName: `${uuid()}.${row.ext}`,
        contentType: row.contentType,
        ownerId,
        onProgress: (n) => {
          if (get(id)?.status === "uploading") patch(id, { progress: Math.max(0, Math.min(100, Math.round(n))) });
        },
      });
      if (!get(id)) return false; // removed mid-upload: the object is orphaned (accepted, as in the app)
      patch(id, { status: "done", progress: 100, result });
      return true;
    } catch (err) {
      // A plain Storage failure (`storage/*`) is a per-file Retry; anything
      // else (token mint, no bucket) is surfaced as a RecoveryView.
      const code = (err as { code?: unknown } | null)?.code;
      if (!(typeof code === "string" && code.startsWith("storage/"))) lastError = err;
      if (get(id)) patch(id, { status: "failed", error: err instanceof UploadsUnavailableError ? "unavailable" : "upload" });
      return false;
    }
  };

  const allDone = () => files.filter(valid).every((f) => f.status === "done");

  return {
    getState: () => state,
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
    async add(kind, picked) {
      const { max } = UPLOAD_LIMITS[kind];
      let room = max - files.filter((f) => f.kind === kind && valid(f)).length;
      let totalRoom = MAX_TOTAL_FILES - files.filter(valid).length;
      let dropped = false;
      let droppedTotal = false;
      const rows: QueuedFile[] = [];
      const toPrepare: Array<[QueuedFile, File]> = [];
      for (const file of picked) {
        const id = `f${++seq}`;
        const error = validateFile(file, kind);
        if (error) {
          rows.push({ id, kind, name: file.name, status: "invalid", progress: 0, error });
          continue;
        }
        if (room <= 0) {
          dropped = true;
          continue;
        }
        if (totalRoom <= 0) {
          droppedTotal = true;
          continue;
        }
        room -= 1;
        totalRoom -= 1;
        const row: QueuedFile = { id, kind, name: file.name, status: "preparing", progress: 0 };
        rows.push(row);
        toPrepare.push([row, file]);
      }
      files = [...files, ...rows];
      tooMany = { ...tooMany, [kind]: dropped };
      overTotal = { ...overTotal, [kind]: droppedTotal };
      emit();
      await Promise.all(toPrepare.map(([row, f]) => prepare(row, f)));
    },
    remove(id) {
      files = files.filter((f) => f.id !== id);
      emit();
    },
    async uploadPending(ownerId) {
      lastError = null;
      const ids = files.filter((f) => f.status === "ready" || f.status === "failed").map((f) => f.id);
      let next = 0;
      let uploaded = 0;
      const worker = async () => {
        while (next < ids.length) {
          const id = ids[next++]!;
          if (await uploadOne(id, ownerId)) uploaded++;
        }
      };
      await Promise.all(Array.from({ length: Math.min(concurrency, ids.length) }, worker));
      // Every file in the batch failed: Storage itself is down, so a per-file
      // Retry alone would not help.
      if (ids.length > 0 && uploaded === 0 && lastError === null) lastError = new UploadsUnavailableError();
      return allDone();
    },
    async retry(id, ownerId) {
      lastError = null;
      if (get(id)?.status !== "failed") return allDone();
      await uploadOne(id, ownerId);
      return allDone();
    },
    count: (kind) => files.filter((f) => f.kind === kind && valid(f)).length,
    allDone,
    preparing: () => files.some((f) => f.status === "preparing"),
    documents() {
      const out: ItemUploadDocument[] = [];
      for (const kind of UPLOAD_KINDS) {
        const shape = DOCUMENT_SHAPE[kind];
        files
          .filter((f) => f.kind === kind && f.status === "done" && f.result)
          .forEach((f, i) => {
            out.push({
              url: f.result!.url,
              location: f.result!.location,
              type: shape.type,
              classification: shape.classification,
              order_index: i,
            });
          });
      }
      return out;
    },
    lastError: () => lastError,
    clear() {
      files = [];
      tooMany = {};
      overTotal = {};
      lastError = null;
      emit();
    },
  };
}

export { createHeicConverter } from "./heic.js";
